/**
 * Service de conversion de devises vers USD
 * Utilise l'API backend pour obtenir les taux de change actuels
 */

import api from './api';

class CurrencyConversionService {
  /**
   * Convertir n'importe quelle devise vers USD
   * @param {number} amount - Montant à convertir
   * @param {string} fromCurrency - Code ISO de la devise (EUR, GBP, CHF, etc.)
   * @returns {Promise<Object>} - Objet contenant le montant en USD et les détails
   * @example
   * const result = await currencyConversionService.convertToUSD(10, 'EUR');
   * console.log(result.amountInUSD); // e.g., 10.95
   * console.log(result.meetsMinimum); // true si >= 10 USD
   */
  async convertToUSD(amount, fromCurrency) {
    try {
      console.log('[CurrencyConversionService] Converting', {
        amount,
        fromCurrency,
        endpoint: '/exchange-rates/convert-to-usd'
      });

      const response = await api.post('/exchange-rates/convert-to-usd', {
        amount: parseFloat(amount),
        fromCurrency: String(fromCurrency).toUpperCase()
      });

      const result = response?.data?.data || response?.data;

      console.log('[CurrencyConversionService] ✅ Conversion successful:', {
        originalAmount: result.originalAmount,
        originalCurrency: result.originalCurrency,
        amountInUSD: result.amountInUSD,
        equivalentInUSD: result.equivalentInUSD,
        meetsMinimum: result.amountInUSD >= 5
      });

      return {
        success: true,
        originalAmount: result.originalAmount,
        originalCurrency: result.originalCurrency,
        amountInUSD: result.amountInUSD,
        equivalentInUSD: result.equivalentInUSD,
        meetsMinimum: result.amountInUSD >= 5, // 🎯 Important: Check if >= $10
        exchangeRate: result.exchangeRate,
        timestamp: result.timestamp
      };
    } catch (error) {
      console.error('[CurrencyConversionService] ❌ Conversion failed:', {
        amount,
        fromCurrency,
        error: error.message,
        status: error?.response?.status,
        data: error?.response?.data
      });

      return {
        success: false,
        error: error?.response?.data?.message || error.message || 'Erreur de conversion',
        originalAmount: amount,
        originalCurrency: fromCurrency
      };
    }
  }

  /**
   * Helper: Vérifier si un montant en devise est >= 10 USD
   * @param {number} amount - Montant à vérifier
   * @param {string} currency - Code ISO de la devise
   * @returns {Promise<boolean>} - true si montant >= 10 USD
   */
  async isAboveMinimumUSD(amount, currency) {
    try {
      const result = await this.convertToUSD(amount, currency);
      if (!result.success) {
        console.warn('[CurrencyConversionService] Could not verify minimum, allowing by default');
        return true; // Laisser passer en cas d'erreur
      }
      return result.meetsMinimum;
    } catch (error) {
      console.error('[CurrencyConversionService] Error checking minimum:', error);
      return true; // Laisser passer en cas d'erreur
    }
  }

  /**
   * Formater un montant USD pour affichage
   * @param {number} usdAmount - Montant en USD
   * @returns {string} - Montant formaté (e.g., "$10.50")
   */
  formatUSD(usdAmount) {
    return `$${(Math.round(usdAmount * 100) / 100).toFixed(2)}`;
  }
}

export default new CurrencyConversionService();
